import React, { useState, useEffect, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import Breadcrumb from '../../components/ui/Breadcrumb';

import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import ReportCategoryCard from './components/ReportCategoryCard';
import ReportBuilder from './components/ReportBuilder';
import KPIWidget from './components/KPIWidget';
import ReportTemplateCard from './components/ReportTemplateCard';
import ExportModal from './components/ExportModal';
import ReportPreviewModal from './components/ReportPreviewModal';
import AnalyticsInsights from './components/AnalyticsInsights';
import InventoryAnalytics from './components/InventoryAnalytics';
import api from '../../utils/api';
import { exportToCSV } from '../../utils/exportUtils';

const Reports = () => {
  const [isReportBuilderOpen, setIsReportBuilderOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [selectedExportReport, setSelectedExportReport] = useState('');
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [dateRange, setDateRange] = useState('last_30_days');
  const [customFrom, setCustomFrom] = useState(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10));
  const [customTo, setCustomTo] = useState(new Date().toISOString().slice(0, 10));
  const [refreshKey, setRefreshKey] = useState(0);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [insightsText, setInsightsText] = useState('');
  const [products, setProducts] = useState([]);
  
  // Define options for select inputs
  const dateRangeOptions = [
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'last_7_days', label: 'Last 7 Days' },
    { value: 'last_30_days', label: 'Last 30 Days' },
    { value: 'this_month', label: 'This Month' },
    { value: 'last_month', label: 'Last Month' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const dateWindow = useMemo(() => {
    const now = new Date();
    let from;
    let to = new Date(now);
    switch (dateRange) {
      case 'today':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'yesterday':
        from = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        to = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'last_7_days':
        from = new Date(now);
        from.setDate(from.getDate() - 6);
        from.setHours(0, 0, 0, 0);
        break;
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'custom':
        from = customFrom ? new Date(`${customFrom}T00:00:00`) : null;
        to = customTo ? new Date(`${customTo}T23:59:59.999`) : null;
        if (!from || Number.isNaN(from.getTime())) {
          from = new Date(now);
          from.setDate(from.getDate() - 29);
          from.setHours(0, 0, 0, 0);
        }
        if (!to || Number.isNaN(to.getTime())) to = new Date(now);
        break;
      default:
        from = new Date(now);
        from.setDate(from.getDate() - 29);
        from.setHours(0, 0, 0, 0);
    }
    return { from, to };
  }, [dateRange, customFrom, customTo, refreshKey]);
  

  
  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await api.get('/products');
        setProducts(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error('Failed to load products for categories', error);
        setProducts([]);
      }
    };
    
    fetchProducts();
  }, [refreshKey]);

  // Generate category options dynamically from products
  const categoryOptions = useMemo(() => {
    const set = new Set();
    products.forEach(p => {
      const cats = Array.isArray(p?.category) ? p.category : (p?.category ? [p.category] : []);
      cats.forEach(c => {
        if (c) set.add(c);
      });
    });
    const options = Array.from(set)
      .sort((a, b) => a.localeCompare(b))
      .map(c => ({ value: c, label: c }));
    return [{ value: 'all', label: 'All Categories' }, ...options];
  }, [products]);

  const reportCategories = [
    { 
      id: 1, 
      title: 'Inventory Valuation', 
      description: 'Current stock value and cost analysis', 
      icon: 'DollarSign', 
      iconColor: '#27AE60', 
      iconBg: 'bg-success/10', 
      status: 'ready', 
      type: 'valuation',
      fields: ['product_name', 'sku', 'barcode', 'category', 'quantity', 'unit_cost', 'total_value']
    },
    { 
      id: 2, 
      title: 'Movement Analysis', 
      description: 'Stock in/out transactions and trends', 
      icon: 'TrendingUp', 
      iconColor: '#4A90A4', 
      iconBg: 'bg-primary/10', 
      status: 'ready', 
      type: 'movement',
      fields: ['product_name', 'barcode', 'date', 'quantity', 'type']
    },
    { 
      id: 4, 
      title: 'Forecasting Reports', 
      description: 'Demand prediction and planning', 
      icon: 'BarChart3', 
      iconColor: '#E67E22', 
      iconBg: 'bg-warning/10', 
      status: 'ready', 
      type: 'forecasting',
      fields: ['product_name', 'barcode', 'current_stock', 'reorder_point', 'forecast_days_remaining', 'recommended_order']
    }
  ];

  // State for KPI data
  const [kpiData, setKpiData] = useState([]);
  const [kpiError, setKpiError] = useState('');
  const [incompleteSalesHistory, setIncompleteSalesHistory] = useState(0);
  
  // Fetch KPI data from API
  useEffect(() => {
    const fetchKpiData = async () => {
      setKpiError('');
      try {
        const { data } = await api.get('/reports/kpi', {
          params: {
            from: dateWindow.from.toISOString(),
            to: dateWindow.to.toISOString(),
            category: categoryFilter === 'all' ? undefined : categoryFilter
          }
        });
        if (data && data.kpiData) {
          setKpiData(data.kpiData);
          setIncompleteSalesHistory(Number(data.incompleteSalesHistory) || 0);
        }
      } catch (error) {
        console.error('Failed to fetch KPI data:', error);
        setKpiError('Analytics could not be loaded. Please refresh to try again.');
      }
    };
    
    fetchKpiData();
  }, [refreshKey, dateWindow, categoryFilter]);

  const reportTemplates = [
    { id: 1, name: 'ABC Analysis', description: 'Categorize inventory by value and importance', icon: 'BarChart3', iconColor: '#4A90A4', iconBg: 'bg-primary/10', difficulty: 'beginner', estimatedTime: '2-3 minutes', dataSources: ['Products', 'Sales', 'Inventory'], outputFormats: ['PDF', 'Excel'], usageCount: 47, rating: 4.8 },
    { id: 2, name: 'Slow-Moving Inventory', description: 'Identify items with low turnover rates', icon: 'Clock', iconColor: '#E67E22', iconBg: 'bg-warning/10', difficulty: 'intermediate', estimatedTime: '3-5 minutes', dataSources: ['Inventory', 'Transactions'], outputFormats: ['PDF', 'Excel', 'CSV'], usageCount: 32, rating: 4.6 },
    { id: 3, name: 'Reorder Recommendations', description: 'Automated purchase suggestions based on stock levels', icon: 'ShoppingCart', iconColor: '#27AE60', iconBg: 'bg-success/10', difficulty: 'advanced', estimatedTime: '5-7 minutes', dataSources: ['Inventory', 'Sales', 'Suppliers'], outputFormats: ['PDF', 'Excel'], usageCount: 28, rating: 4.9 }
  ];

  const handleExportReport = async (exportConfig) => {
    if (exportConfig.format === 'csv') {
      const result = await exportToCSV('/export/products.csv', 'products-export', {
        params: categoryFilter === 'all' ? {} : { category: categoryFilter }
      });
      if (!result.success) throw result.error;
    } else {
      const { data } = await api.get('/reports/analytics');
      const matchesCategory = (category) => categoryFilter === 'all'
        || (Array.isArray(category) ? category.includes(categoryFilter) : category === categoryFilter);
      const filteredData = {
        filters: {
          from: dateWindow.from.toISOString(),
          to: dateWindow.to.toISOString(),
          category: categoryFilter
        },
        products: data.products.filter(product => matchesCategory(product.category)),
        sales: data.sales.filter(sale => {
          const createdAt = new Date(sale.createdAt);
          return matchesCategory(sale.category) && createdAt >= dateWindow.from && createdAt <= dateWindow.to;
        })
      };
      const blob = new Blob([JSON.stringify(filteredData, null, 2)], { type: 'application/json' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `inventory-analytics-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
    }
  };

  const handleSaveCustomReport = async (config) => {
    try {
      const { data } = await api.post('/reports/generate', {
        ...config,
        type: 'inventory'
      });
      setIsReportBuilderOpen(false);
      handlePreview(data);
    } catch (error) {
      console.error('Failed to generate custom report:', error);
      throw error;
    }
  };

  const handleViewInsightDetails = async () => {
    try {
      const { data } = await api.post('/analysis/insights', { question: 'Highlight top issues and opportunities from current inventory' });
      setInsightsText(data?.insights || '');
    } catch (e) {
      console.error('AI analysis failed', e);
    }
  };

  const handleExportClick = (reportName) => {
    setSelectedExportReport(reportName);
    setIsExportModalOpen(true);
  };
  
  const handlePreview = (report) => {
    if (report && report.id) {
      setSelectedReportId(report.id);
      setIsPreviewModalOpen(true);
    }
  };

  return (
    <>
      <div className="min-h-screen bg-background">
        <Helmet>
          <title>Reports - InventoryTracker</title>
          <meta name="description" content="Comprehensive inventory analytics and reporting with customizable data visualization and export functions" />
        </Helmet>
        <div className="w-full px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6 sm:mb-8">
              <div>
                <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-foreground mb-1 sm:mb-2">Reports & Analytics</h1>
                <p className="text-xs sm:text-sm lg:text-base text-muted-foreground">
                  Generate comprehensive inventory reports and gain insights from your data
                </p>
              </div>
              <div className="flex items-center space-x-2 sm:space-x-3 mt-3 lg:mt-0">
                <Button
                  variant="outline"
                  size="sm"
                  iconName="Download"
                  iconPosition="left"
                  iconSize={14}
                  className="text-xs sm:text-sm px-2.5 sm:px-4"
                  onClick={() => handleExportClick('Current Dashboard')}
                >
                  Export
                </Button>
                <Button
                  variant="default"
                  size="sm"
                  iconName="Plus"
                  iconPosition="left"
                  iconSize={14}
                  className="text-xs sm:text-sm px-2.5 sm:px-4"
                  onClick={() => setIsReportBuilderOpen(true)}
                >
                  Create Custom Report
                </Button>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-card border border-border rounded-lg p-4 sm:p-6 mb-6 sm:mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
                <div>
                  <Input
                    placeholder="Search reports..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e?.target?.value)}
                    className="text-sm"
                  />
                </div>
                <Select
                  options={dateRangeOptions}
                  value={dateRange}
                  onChange={setDateRange}
                  placeholder="Select date range"
                />

                {dateRange === 'custom' && (
                  <>
                    <Input aria-label="Start date" type="date" value={customFrom} onChange={(event) => setCustomFrom(event.target.value)} />
                    <Input aria-label="End date" type="date" value={customTo} onChange={(event) => setCustomTo(event.target.value)} />
                  </>
                )}

                <Select
                  options={categoryOptions}
                  value={categoryFilter}
                  onChange={setCategoryFilter}
                  placeholder="Select category"
                />
              </div>
            </div>

            {/* KPI Dashboard */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground">Key Performance Indicators</h2>
                <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} className="text-xs sm:text-sm px-2.5 sm:px-3" onClick={() => setRefreshKey(value => value + 1)}>
                  Refresh Data
                </Button>
              </div>
              {kpiError && <p role="alert" className="mb-4 text-sm text-destructive">{kpiError}</p>}
              {incompleteSalesHistory > 0 && (
                <p role="status" className="mb-4 text-sm text-warning">
                  Some older checkout records do not contain per-sale quantities and are excluded from sales analytics.
                </p>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {kpiData?.map((kpi) => (
                  <KPIWidget
                    key={kpi?.id}
                    kpi={kpi}
                    onDrillDown={() => {}}
                  />
                ))}
              </div>
            </div>

            {/* Report Categories */}
            <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground">Report Categories</h2>
                <Button variant="ghost" size="sm" iconName="Grid" iconPosition="left" iconSize={14} className="text-xs sm:text-sm px-2.5 sm:px-3">
                  View All
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                {reportCategories
                  .filter(report => report.title.toLowerCase().includes(searchQuery.toLowerCase()))
                  .map((report) => (
                  <ReportCategoryCard
                    key={report?.id}
                    report={report}
                    filters={{
                      ...(categoryFilter === 'all' ? {} : { category: categoryFilter }),
                      ...(report.type === 'movement' ? {
                        from: dateWindow.from.toISOString(),
                        to: dateWindow.to.toISOString()
                      } : {})
                    }}
                    onPreview={handlePreview}
                  />
                ))}
                {reportCategories.filter(report => report.title.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 && (
                  <p className="col-span-full text-sm text-muted-foreground">No reports match your search.</p>
                )}
              </div>
            </div>



            {/* Inventory Analytics */}
            <InventoryAnalytics
              dateFrom={dateWindow.from}
              dateTo={dateWindow.to}
              categoryFilter={categoryFilter}
              refreshKey={refreshKey}
            />
            
            {/* Analytics Insights */}
            {/* <div className="mb-6 sm:mb-8">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-base sm:text-lg lg:text-xl font-semibold text-foreground">AI Insights</h2>
                <Button variant="outline" size="sm" iconName="RefreshCw" iconPosition="left" iconSize={14} className="text-xs sm:text-sm px-2.5 sm:px-3" onClick={handleViewInsightDetails}>
                  Generate Insights
                </Button>
              </div>
              {insightsText ? (
                <div className="bg-card border border-border rounded-lg p-3 sm:p-4 whitespace-pre-wrap text-xs sm:text-sm">{insightsText}</div>
              ) : (
                <AnalyticsInsights 
                  insights={[
                    { id: 1, title: 'Low Stock Alert', description: 'Low Stock alert ', severity: 'high', icon: 'AlertTriangle', iconColor: '#E74C3C' },
                    { id: 2, title: 'Seasonal Trend', description: 'Seasonal Trend', severity: 'medium', icon: 'TrendingUp', iconColor: '#27AE60' },
                    { id: 3, title: 'Overstocked Items', description: 'Overstocked Items', severity: 'medium', icon: 'Package', iconColor: '#F39C12' }
                  ]} 
                  onViewDetails={handleViewInsightDetails} 
                />
              )}
            </div> */}
          </div>
        </div>
      <ReportBuilder
        isOpen={isReportBuilderOpen}
        onClose={() => setIsReportBuilderOpen(false)}
        onSave={handleSaveCustomReport}
      />
      <ExportModal
        isOpen={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        onExport={handleExportReport}
        reportName={selectedExportReport}
        categoryFilter={categoryFilter}
      />
      <ReportPreviewModal
        isOpen={isPreviewModalOpen}
        onClose={() => setIsPreviewModalOpen(false)}
        reportId={selectedReportId}
      />
    </>
  );
};

export default Reports;